import {
  Button,
  ButtonDynamicContent,
  DatePicker,
  Divider,
  FilterButton,
  Popover,
  SelectField,
} from 'omakase';
import { CalendarWithQuickFilter } from 'components/search/calendar/CalendarWithQuickFilter';
import { LocalDate } from 'js-joda';
import { NULL_PARTY_SIZE_LABEL } from 'components/www/availability-search/SearchHeader';
import { Theme, css } from '@emotion/react';
import { TimeOption } from 'utils/timeUtils';
import { popoverContent } from 'components/www/availability-search/searchBarAndButtonCssDefs';
import React from 'react';

const selectVerticalSpacing = (theme: Theme) => css`
  margin-top: ${theme.spacing(2.5)}px;
`;

const actions = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

interface Props {
  showDate: boolean;
  showTime: boolean;
  showSize: boolean;
  dateLabel: React.ReactNode;
  timeLabel: React.ReactNode;
  guestsLabel: React.ReactNode;
  onClickSearch(e: React.MouseEvent<HTMLButtonElement>): void;
  onClose(): void;
  onChangeDate(date: string): void;
  onChangeTime(time: string): void;
  onChangeSize(size: string): void;
  onSearch(): void;
  isOpen: boolean;
  anchorEl?: HTMLButtonElement;
  startDate: LocalDate;
  endDate: LocalDate;
  dateEnd?: string;
  date?: string;
  time?: string;
  displayedTimes: TimeOption[];
  size?: string;
  sizeOptions: Option[];
  showTimeFilterLabel: boolean;
  onChangeDateRange?(startDate?: LocalDate, endDate?: LocalDate): void;
  useDateRangePicker: boolean;
}

const DateTimeGuestsSearchBarButton: React.FunctionComponent<Props> = ({
  showDate,
  showTime,
  showSize,
  dateLabel,
  timeLabel,
  guestsLabel,
  onClickSearch,
  onChangeDate,
  onChangeSize,
  onChangeTime,
  onSearch,
  isOpen,
  onClose,
  anchorEl,
  startDate,
  endDate,
  dateEnd,
  date,
  time,
  displayedTimes,
  size,
  sizeOptions,
  showTimeFilterLabel,
  onChangeDateRange,
  useDateRangePicker,
}) => {
  const selectedDateAsLocalDate = dateEnd || !date ? undefined : LocalDate.parse(date);
  const handleChangeDate = (date: LocalDate | undefined) => {
    date && onChangeDate(date.toString());
  };

  return (
    <>
      <FilterButton
        aria-haspopup
        aria-label="Change date, time, and party-size"
        onClick={onClickSearch}
        selected={!!(dateLabel || timeLabel || guestsLabel)}
        data-testid="filter-desktop-button"
      >
        {showDate && <ButtonDynamicContent placeholder="Date">{dateLabel}</ButtonDynamicContent>}
        {showTime && showTimeFilterLabel && (
          <ButtonDynamicContent placeholder="Time">{timeLabel}</ButtonDynamicContent>
        )}
        {showSize && (
          <ButtonDynamicContent placeholder={NULL_PARTY_SIZE_LABEL}>
            {guestsLabel !== NULL_PARTY_SIZE_LABEL ? guestsLabel : ''}
          </ButtonDynamicContent>
        )}
      </FilterButton>
      <Popover
        anchorEl={anchorEl}
        open={isOpen}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        maxWidthPx={390}
        minWidthPx={390}
      >
        <div css={popoverContent}>
          <>
            {showDate &&
              (useDateRangePicker ? (
                <CalendarWithQuickFilter
                  startDate={startDate}
                  endDate={endDate}
                  date={date}
                  dateEnd={dateEnd}
                  onChangeDate={onChangeDate}
                  onChangeDateRange={onChangeDateRange}
                />
              ) : (
                <DatePicker
                  latestSelectableDate={endDate}
                  selectionType="single"
                  selectedDate={selectedDateAsLocalDate}
                  onDateSelected={handleChangeDate}
                />
              ))}
            {showTime && (
              <div css={selectVerticalSpacing}>
                <SelectField
                  fullWidth
                  name="time"
                  id="time"
                  value={time}
                  variant="inverse"
                  label="Time"
                  onChange={(
                    event: React.ChangeEvent<{
                      name?: string | undefined;
                      value: unknown;
                    }>
                  ) => onChangeTime(event.target.value as string)}
                  inputProps={{
                    'aria-label': 'Time',
                  }}
                >
                  <option value="" />
                  {displayedTimes.map((time) => (
                    <option key={time.value} value={time.value}>
                      {time.label}
                    </option>
                  ))}
                </SelectField>
              </div>
            )}
            {showSize && (
              <div css={selectVerticalSpacing}>
                <SelectField
                  fullWidth
                  name="size"
                  id="Size"
                  value={size}
                  variant="inverse"
                  label="Party size"
                  onChange={(
                    event: React.ChangeEvent<{
                      name?: string | undefined;
                      value: unknown;
                    }>
                  ) => onChangeSize(event.target.value as string)}
                  inputProps={{
                    'aria-label': 'Size',
                  }}
                >
                  <option value="">Party size</option>
                  {sizeOptions.map((size) => (
                    <option key={size.value} value={size.value}>
                      {size.label}
                    </option>
                  ))}
                </SelectField>
              </div>
            )}
          </>
          <Divider mb={2.5} mt={2.5} />
          <div css={actions}>
            <Button variant="primary" size="medium" onClick={onSearch}>
              Update search
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default DateTimeGuestsSearchBarButton;
